import React, { useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';

interface DeleteFeedDialogProps {
  isOpen: boolean;
  feedName: string;
  onClose: () => void;
  onConfirm: (deleteObjects: boolean) => void;
  isDeleting: boolean;
}

export default function DeleteFeedDialog({
  isOpen,
  feedName,
  onClose,
  onConfirm,
  isDeleting
}: DeleteFeedDialogProps) {
  const [deleteObjects, setDeleteObjects] = useState(false);

  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm(deleteObjects);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
        
        <div className="relative w-full max-w-lg rounded-lg bg-white shadow-xl">
          <div className="flex items-start p-6">
            <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            
            <button
              onClick={onClose}
              className="absolute right-4 top-4 text-gray-400 hover:text-gray-500"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          <div className="px-6 pb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Delete Feed
            </h3>
            
            <p className="text-sm text-gray-500 mb-4">
              Are you sure you want to delete the feed <strong>"{feedName}"</strong>?
              This action cannot be undone and will stop all future polling for this feed.
            </p>
            
            <div className="mb-6">
              <label className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  checked={deleteObjects}
                  onChange={(e) => setDeleteObjects(e.target.checked)}
                  className="mt-0.5 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <div>
                  <span className="text-sm font-medium text-gray-900">
                    Also delete all objects from this feed
                  </span>
                  <p className="text-xs text-gray-500 mt-1">
                    This will permanently delete all STIX objects that were ingested from this feed.
                    Objects will be removed from both the database and file storage.
                  </p>
                </div>
              </label>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                disabled={isDeleting}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={isDeleting}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {isDeleting ? 'Deleting...' : 'Delete Feed'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}